import { bus } from './event-bus.js';

const initialState = {
  playerState: 'idle',    // idle | loading | playing | paused | error | complete
  environment: 'desktop',
  adOnStage: false,
  adType: null,           // linear | nonlinear | null
  adPaused: false,
  volume: 1,
  muted: false,
  adDuration: -1,
  adCurrentTime: -1,
  adRemainingTime: -1,
  adTagUrl: '',
  rawXml: null,
  adMediaUrl: '',
  adContentType: '',
  adTitle: '',
  adSystem: null,
  adDescription: '',
  adMediaWidth: -1,
  adMediaHeight: -1,
  clickThroughUrl: '',
  isSkippableAd: false,
  adSkippableState: false,
  skipTimeOffset: -1,
  adLinear: true,
  audioSignalDetected: false,
  audioSignalLevel: 0,
  adErrorMessage: '',
  adVastErrorCode: -1,
  adErrorType: '',
  validationStatus: 'pending', // pending | running | passed | failed
  validationResults: null,
  sessionStatus: 'idle', // idle | running | complete | error
  sessionSourceType: null, // tag | xml | preset | null
  sessionSourceValue: '',
  sessionStartedAt: null,
  sessionEndedAt: null,
  sessionDurationMs: 0,
  diagnosticsCount: 0,
};

class StateStore {
  constructor() {
    this._state = { ...initialState };
    this._events = [];
    this._networkLog = [];
    this._stateSnapshots = [];
    this._lastLiveSnapshot = null;
    this._finalSnapshot = null;
    this._sessionStartTime = null;
    this._sessionId = null;
  }

  get state() {
    return { ...this._state };
  }

  get events() {
    return [...this._events];
  }

  get networkLog() {
    return [...this._networkLog];
  }

  get stateSnapshots() {
    return [...this._stateSnapshots];
  }

  get lastLiveSnapshot() {
    return this._lastLiveSnapshot ? { ...this._lastLiveSnapshot } : null;
  }

  get finalSnapshot() {
    return this._finalSnapshot ? { ...this._finalSnapshot } : null;
  }

  update(partial) {
    const prev = { ...this._state };
    Object.assign(this._state, partial);
    bus.emit('state:changed', { prev, current: this.state, changed: Object.keys(partial) });
  }

  addEvent(type, data = {}) {
    const entry = {
      type,
      timestamp: this._elapsed(),
      data,
      id: this._events.length,
    };
    this._events.push(entry);
    bus.emit('event:logged', entry);
    return entry;
  }

  addNetworkEntry(url, initiator = 'tracking') {
    const entry = {
      url,
      timestamp: this._elapsed(),
      initiator,
      id: this._networkLog.length,
    };
    this._networkLog.push(entry);
    bus.emit('network:logged', entry);
    return entry;
  }

  takeSnapshot() {
    const snapshot = {
      timestamp: this._elapsed(),
      state: this.state,
    };
    this._stateSnapshots.push(snapshot);
    return snapshot;
  }

  captureLiveSnapshot(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return;
    this._lastLiveSnapshot = {
      ...(this._lastLiveSnapshot || {}),
      ...snapshot,
    };
  }

  freezeFinalSnapshot(snapshot = this._lastLiveSnapshot) {
    if (!snapshot || typeof snapshot !== 'object') return;
    this._finalSnapshot = { ...snapshot };
  }

  clearSnapshots() {
    this._lastLiveSnapshot = null;
    this._finalSnapshot = null;
  }

  startSession(meta = {}) {
    const env = this._state.environment;
    this._sessionStartTime = performance.now();
    this._sessionId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36);
    this._events = [];
    this._networkLog = [];
    this._stateSnapshots = [];
    this.clearSnapshots();
    Object.assign(this._state, initialState);
    Object.assign(this._state, {
      environment: env,
      sessionStatus: 'running',
      sessionSourceType: meta.sourceType || (meta.rawXml ? 'xml' : 'tag'),
      sessionSourceValue: meta.sourceValue || meta.tag || '',
      sessionStartedAt: new Date().toISOString(),
      sessionEndedAt: null,
      sessionDurationMs: 0,
      diagnosticsCount: 0,
    });
    bus.emit('session:started');
  }

  finishSession(status = 'complete') {
    if (!this._sessionStartTime || this._state.sessionStatus !== 'running') return;
    this._state.sessionStatus = status;
    this._state.sessionEndedAt = new Date().toISOString();
    this._state.sessionDurationMs = this._elapsed();
    bus.emit('session:finished', {
      sessionId: this._sessionId,
      status,
      durationMs: this._state.sessionDurationMs,
    });
  }

  getSessionData() {
    const nowDuration = this._state.sessionStatus === 'running' ? this._elapsed() : this._state.sessionDurationMs;
    return {
      sessionId: this._sessionId,
      timestamp: new Date().toISOString(),
      lifecycle: {
        status: this._state.sessionStatus,
        startedAt: this._state.sessionStartedAt,
        endedAt: this._state.sessionEndedAt,
        durationMs: nowDuration,
      },
      config: {
        tag: this._state.adTagUrl,
        environment: this._state.environment,
        sourceType: this._state.sessionSourceType,
        sourceValue: this._state.sessionSourceValue,
      },
      events: this.events,
      stateSnapshots: this.stateSnapshots,
      networkLog: this.networkLog,
      validationResults: this._state.validationResults,
      adInfo: {
        system: this._state.adSystem,
        title: this._state.adTitle,
        duration: this._state.adDuration,
        mediaUrl: this._state.adMediaUrl,
        contentType: this._state.adContentType,
        linear: this._state.adLinear,
        mediaWidth: this._state.adMediaWidth,
        mediaHeight: this._state.adMediaHeight,
      },
      errors: this._events
        .filter(e => e.type === 'aderror')
        .map(e => ({ code: e.data.code, message: e.data.message, timestamp: e.timestamp })),
    };
  }

  reset() {
    Object.assign(this._state, initialState);
    this._events = [];
    this._networkLog = [];
    this._stateSnapshots = [];
    this.clearSnapshots();
    this._sessionStartTime = null;
    this._sessionId = null;
    bus.emit('state:reset');
  }

  _elapsed() {
    if (!this._sessionStartTime) return 0;
    return Math.round(performance.now() - this._sessionStartTime);
  }
}

export const store = new StateStore();
